import "./hashgraph-sdk-patch";

import {
  DAppConnector,
  HederaJsonRpcMethod,
  HederaSessionEvent,
  HederaChainId,
} from "@hashgraph/hedera-wallet-connect";
import { LedgerId, AccountId, Signer, Client, Transaction } from "@hashgraph/sdk";
import { proto } from "@hashgraph/proto";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateUserData, updateWalletData } from "store";
import { useHederaBalance } from "../../hooks/useHederaBalance";

const useHederaWalletConnect = ({
  projectId,
  hbarxId,
  onDisconnect,
}: {
  projectId: string;
  hbarxId: string;
  onDisconnect?: (type: "session_delete" | "session_expire") => void;
}) => {
  const connectorRef = useRef<DAppConnector>();
  const dispatch = useDispatch();
  const [accountId, setAccountId] = useState<AccountId | null>(null);
  const [accountIdString, setAccountIdString] = useState<string | null>(null);
  const [signer, setSigner] = useState<Signer | null>(null);
  const [wcTopic, setWcTopic] = useState<string | null>(null);

  const client = useMemo(() => Client.forMainnet(), []);

  const { data: balances, isLoading: isBalancesLoading } = useHederaBalance({
    accountId,
    client,
    hbarxId,
    wcTopic,
  });

  useEffect(() => {
    if (balances) {
      const hbarBalBN = balances.hbar.toBigNumber();
      dispatch(
        updateUserData({
          tokenAmount: hbarBalBN.toString(),
          tokenXAmount: balances.hbarx.toString(),
        })
      );
    } else {
      dispatch(
        updateUserData({
          tokenAmount: "0",
          tokenXAmount: "0",
        })
      );
    }
  }, [dispatch, balances]);

  useEffect(() => {
    dispatch(updateUserData({ isLoading: isBalancesLoading }));
  }, [dispatch, isBalancesLoading]);

  const handleSession = useCallback(
    (session: any) => {
      const acc = session.namespaces?.hedera?.accounts?.[0];
      if (acc) {
        const parts = acc.split(":");
        const network = parts[1];
        const id = parts[2];
        localStorage.setItem("hederaAccountId", id);
        localStorage.setItem("hederaNetwork", network);
        const acctId = AccountId.fromString(id);
        const accIdWithChecksum = acctId.toStringWithChecksum(client);
        setAccountId(acctId);
        setAccountIdString(accIdWithChecksum);

        setWcTopic(session.topic);

        const signer = connectorRef.current?.signers.find(
          (s) => s.getAccountId().toString() === acctId.toString()
        );

        // web-sdk freezes transactions with a node-account-id list that
        // contains duplicates. Transaction.fromBytes() de-duplicates the
        // node ids but keeps every signed-transaction entry, so the wallet
        // receives a TransactionList whose node count != signed-tx count.
        // HashPack then indexes out of bounds while executing and throws
        // "Cannot read properties of undefined (reading 'execute')",
        // returning 9000 INVALID_PARAMS. Collapse the payload to a single
        // node so the wallet always gets a clean 1-entry TransactionList.
        if (signer && !(signer as any).__singleNodePatched) {
          (signer as any).__singleNodePatched = true;
          const originalCall = (signer as any).call.bind(signer);
          (signer as any).call = (request: any) => {
            try {
              const list = proto.TransactionList.decode(request.toBytes());
              if (list.transactionList && list.transactionList.length > 1) {
                const singleBytes = proto.TransactionList.encode({
                  transactionList: [list.transactionList[0]],
                }).finish();
                return originalCall(Transaction.fromBytes(singleBytes));
              }
            } catch {
              // fall through and send the request unmodified
            }
            return originalCall(request);
          };
        }

        setSigner(signer ?? null);

        return acctId.toString();
      }

      return null;
    },
    [client]
  );

  const deleteSession = useCallback(
    (type: "session_delete" | "session_expire") => {
      dispatch(
        updateWalletData({
          connectorID: null,
          walletName: null,
          isConnected: false,
          walletAddress: null,
        })
      );

      setAccountId(null);
      setAccountIdString(null);
      setSigner(null);
      setWcTopic(null);

      localStorage.removeItem("hederaAccountId");
      localStorage.removeItem("hederaNetwork");

      onDisconnect?.(type);
    },
    [dispatch, onDisconnect]
  );

  const init = useCallback(async () => {
    if (!connectorRef.current) {
      connectorRef.current = new DAppConnector(
        {
          name: "Stader Labs",
          description:
            "Stader Labs brings Hedera (HBAR) Liquid Staking, enabling you to earn rewards by staking your HBAR tokens. Learn about HBAR staking and start staking with Stader Labs",
          url: "https://staderlabs.com/hedera",
          icons: ["https://www.staderlabs.com/assets/images/stader-icon.svg"],
        },
        LedgerId.MAINNET,
        projectId,
        Object.values(HederaJsonRpcMethod),
        [HederaSessionEvent.ChainChanged, HederaSessionEvent.AccountsChanged],
        [HederaChainId.Mainnet]
      );
    }

    await connectorRef.current.init({ logger: "error" });
    connectorRef.current.onSessionIframeCreated = handleSession;

    // * For disconnect and session expire
    const wcClient = connectorRef.current.walletConnectClient;

    wcClient?.on("session_expire", () => {
      deleteSession("session_expire");
    });

    wcClient?.on("session_delete", () => {
      deleteSession("session_delete");
    });
  }, [deleteSession, handleSession, projectId]);

  const connect = useCallback(async () => {
    const session = await connectorRef.current?.openModal();
    return handleSession(session);
  }, [handleSession]);

  const disconnect = useCallback(() => {
    connectorRef.current?.disconnect(wcTopic ?? "");
  }, [wcTopic]);

  return {
    init,
    connect,
    disconnect,
    wcTopic,
    accountId,
    accountIdString,
    signer,
    client,
  };
};

export { useHederaWalletConnect };
